import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useReducer from '@store/useReducer'
import * as EDLStore from '@store/edl/edl'

import { EtatDesLieuxTypeEnum } from '@api/enum'

import { isBefore } from 'date-fns'

interface Props {
  manifestation: ManifestationResponse
  onClick: () => void
  places: LocalisationResponse[]
  isIN: boolean
  existingEDL?: boolean
}

const ManifestationCard = ({ manifestation, onClick, places, isIN }: Props) => {
  const i18n = useI18n()
  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const spaces = places.filter((p) => !p.parentId && p.enabled && manifestation.espaceIds.includes(p.id)).length
  const EDLs = useReducer(EDLStore.store, (s) => s.EDLs)

  const dateOpen = isIN ? new Date(manifestation.dateOuverturePublic) : undefined
  const dateClose = !isIN ? new Date(manifestation.dateFermeturePublic) : undefined

  const isPast = !!dateOpen && isBefore(dateOpen, Date.now())

  const existingEDLs = React.useMemo(
    () =>
      EDLs.filter(
        (edl) =>
          edl.manifestationId === manifestation.id &&
          edl.type === (isIN ? EtatDesLieuxTypeEnum.ENTREE : EtatDesLieuxTypeEnum.SORTIE)
      ),
    [manifestation, EDLs]
  )

  return (
    <Container key={manifestation.id} activeOpacity={0.9} onPress={onClick}>
      <InfoContainer>
        <Name numberOfLines={2} ellipsizeMode="tail">
          {manifestation.libelle}
        </Name>

        <SubInfos>
          <EventText>{i18n.t('screens.edlList.spacesCount', { count: spaces })}</EventText>
        </SubInfos>

        <SubInfos>
          {existingEDLs.length > 0 && (
            <IconContainer>
              <Icon name="document" size={20} color={theme.colors.closed} />
            </IconContainer>
          )}

          <EventText primary>{i18n.t(`screens.edlList.${isIN ? 'entry' : 'exit'}`)}</EventText>

          <EventContainer>
            <EventText>
              {!!dateOpen && i18n.t(`screens.edlList.${isPast ? 'openPast' : 'openFuture'}`, { date: dateOpen })}
              {!!dateClose && i18n.t('screens.edlList.closeDate', { date: dateClose })}
            </EventText>
            {existingEDLs.length > 0 && (
              <EventText>
                {i18n.t(`screens.edlList.exist.${isIN ? 'entry' : 'exit'}`, { count: existingEDLs.length })}
              </EventText>
            )}
          </EventContainer>
        </SubInfos>
      </InfoContainer>

      {isPhone && (
        <IconContainer>
          <Icon name="arrow" size={20} color={theme.colors.primary} />
        </IconContainer>
      )}
    </Container>
  )
}

export default ManifestationCard

const Container = styled(TouchableOpacity)`
  margin: 4px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '24px')};
  ${(props) => (props.theme.breakpoint !== 'phone' ? `width:360px;` : 'flex-direction: row; align-items: center;')}
  ${(props) => props.theme.constants.shadow};
  justify-content: space-between;
`
const InfoContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'flex: 1;' : 'flex-grow: 1;')};
`

const Name = styled(Text)<{ isList?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-grow: 1;'};
`

const EventText = styled(Text)<{ primary?: boolean; row?: boolean; padding?: boolean }>`
  ${(props) => !!props.padding && 'padding-right: 32px;'}
  ${(props) => !props.padding && !props.row && 'padding-right: 8px;'}
  ${(props) => props.theme.fonts.Footnote};
  ${(props) => !props.primary && 'flex: 1;'}

  color: ${(props) => (props.primary ? props.theme.colors.primary : props.theme.colors.secondaryText)};
`

const IconContainer = styled(View)`
  padding-right: 16px;
`

const SubInfos = styled(View)`
  flex-direction: row;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? '8' : '12')}px;
  align-items: center;
`

const EventContainer = styled(View)`
  flex: 1;
`
